import React, { useEffect, useState, useContext, useMemo } from "react";
import { 
    Box, Button, Container, Typography, Dialog, DialogTitle, DialogContent, TextField, 
    DialogActions, Tabs, Tab, FormControl, InputLabel, Select, MenuItem,
    InputAdornment, CircularProgress, Grid, Divider, Stack, Card, Avatar,
    useTheme
} from "@mui/material";
import { LoadingButton } from "@mui/lab";

// Iconos
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BadgeIcon from '@mui/icons-material/Badge';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faUsers, faUserCheck, faUserMinus, faUserPlus } from "@fortawesome/free-solid-svg-icons";

// Controllers y Context
import { GetEmployees, CreateEmployee, UpdateEmployee, DeleteEmployee } from "../../../controllers/company/EmployeesController";
import { SearchPerson, CreatePerson } from "../../../controllers/company/crm/CustomersController";
import UserContext from "../../../navigation/context/userContext";

// Componentes
import EmployeesTable from "./EmployeesTable"; 
import WeeklyScheduleEditor from "../../../components/schedules/WeeklyScheduleEditor";
import QUDProgress from "../../../components/progress";
import ResponseComponent from "../../../components/responseComponent";
import CustomBreadcrumbs from "../../../components/breadcrumbs";

const initialForm = {
    id: null, position: '', status: 1, first_name: '', last_name: '', email: '', phone: '', entity_uuid: null
};

export default function Employees() {
    const theme = useTheme();
    const { userData } = useContext(UserContext);
    
    // Estados principales
    const [employees, setEmployees] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    
    // Estados del Modal
    const [open, setOpen] = useState(false);
    const [tabValue, setTabValue] = useState(0); 
    const [form, setForm] = useState(initialForm);
    const [isSearching, setIsSearching] = useState(false);
    const [emailStatus, setEmailStatus] = useState('initial');
    const [isSaving, setIsSaving] = useState(false);
    
    // Estado de respuesta
    const [response, setResponse] = useState({ show: false, success: false, message: '' });

    useEffect(() => { 
        window.scrollTo(0, 0);
        loadEmployees(); 
    }, []);

    const loadEmployees = async () => {
        setIsLoading(true);
        const res = await GetEmployees();
        if (res.success) setEmployees(res.employees || []);
        setIsLoading(false);
    };

    // --- CÁLCULO DE ESTADÍSTICAS (Memoizado) ---
    const stats = useMemo(() => {
        const total = employees.length;
        const active = employees.filter(e => e.status === 1).length;
        return { total, active, inactive: total - active };
    }, [employees]);

    // --- FUNCIONES DEL MODAL (Iguales a tu código original) ---
    const handleOpen = (emp = null) => {
        if(emp) {
            setForm({
                id: emp.id,
                first_name: emp.person?.name || '', last_name: emp.person?.last_name || '',
                email: emp.person?.email || '', phone: emp.person?.phone || '',
                position: emp.position, status: emp.status, entity_uuid: emp.person?.uuid
            });
            setEmailStatus('found');
        } else {
            setForm(initialForm); setEmailStatus('initial');
        }
        setTabValue(0); setOpen(true);
    };

    const handleClose = () => setOpen(false);

    const handleEmailBlur = async () => {
        if (form.id || !form.email.includes('@')) return;
        setIsSearching(true);
        const res = await SearchPerson(form.email);
        setIsSearching(false);
        if (res.success && res.data && res.data.length > 0) {
            const person = res.data[0];
            setForm(prev => ({ ...prev, first_name: person.name, last_name: person.last_name, phone: person.phone, entity_uuid: person.uuid }));
            setEmailStatus('found');
        } else {
            setEmailStatus('not_found'); setForm(prev => ({ ...prev, entity_uuid: null }));
        }
    };

    const handleChange = (e) => { setForm({ ...form, [e.target.name]: e.target.value }); };

    const handleSubmit = async () => {
        setIsSaving(true); let res;
        if (form.id) {
            res = await UpdateEmployee(form.id, { position: form.position, status: form.status, first_name: form.first_name, last_name: form.last_name, phone: form.phone });
        } else {
            let uuidToLink = form.entity_uuid;
            if (emailStatus === 'not_found') {
                const newPersonRes = await CreatePerson({ name: form.first_name, last_name: form.last_name, email: form.email, phone: form.phone });
                if (newPersonRes.success) uuidToLink = newPersonRes.data.uuid;
                else { setIsSaving(false); setResponse({ show: true, success: false, message: newPersonRes.message }); return; }
            }
            res = await CreateEmployee({ entity_uuid: uuidToLink, position: form.position, status: form.status });
        }
        setIsSaving(false);
        if (res.success) { 
            setResponse({ show: true, success: true, message: res.message }); 
            if (!form.id) handleClose(); 
            loadEmployees(); 
        } else { 
            setResponse({ show: true, success: false, message: res.message }); 
        }
    };
    
    const handleDelete = async (id) => {
        if(!window.confirm("¿Desvincular empleado?")) return;
        setIsLoading(true);
        const res = await DeleteEmployee(id);
        if(res.success) await loadEmployees(); 
        setIsLoading(false);
    };

    // Estilo unificado para las tarjetas KPI
    const cardStyle = {
        elevation: 0,
        sx: { border: '1px solid #e0e0e0', borderRadius: 3, p: 2.5, bgcolor: '#ffffff' }
    };

    // --- RENDERIZADO DE LA VISTA ---
    return (
        <Container maxWidth="xl" sx={{ mt: 4, mb: 8 }}>
            
            <QUDProgress open={isLoading && employees.length === 0} />

            <CustomBreadcrumbs 
                returnTo='/' 
                crumbs={[{ name: 'Empleados', link: '/employees' }]}
            />
            
            {/* --- HEADER PRINCIPAL --- */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3, mb: 4 }}>
                <Box>
                    <Typography variant="h5" fontWeight={700} sx={{ color: '#222' }}>
                        Equipo de Trabajo
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#666', mt: 0.5 }}>
                        Administra a tus asociados, horarios y nivel de acceso en la plataforma.
                    </Typography>
                </Box>
                
                <Button
                    variant='contained'
                    color="primary"
                    startIcon={<FontAwesomeIcon icon={faPlus} />}
                    onClick={() => handleOpen()}
                    sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, px: 3, py: 1 }}
                >
                    Agregar Empleado
                </Button>
            </Box>

            {/* --- CABECERA DE ESTADÍSTICAS (KPIs) --- */}
            {employees.length > 0 && (
                <Grid container spacing={3} mb={4}>
                    {/* KPI 1: Total Empleados */}
                    <Grid item xs={12} sm={4}>
                        <Card {...cardStyle}>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Avatar variant='rounded' sx={{ width: 44, height: 44, bgcolor: '#f0f4f8', color: '#1976d2', borderRadius: 2 }}>
                                    <FontAwesomeIcon icon={faUsers} size="sm" />
                                </Avatar>
                                <Box flexGrow={1}>
                                    <Typography variant="caption" sx={{ color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        Total Empleados
                                    </Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#222', my: 0.2 }}>
                                        {stats.total}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Card>
                    </Grid>

                    {/* KPI 2: Activos */}
                    <Grid item xs={12} sm={4}>
                        <Card {...cardStyle}>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Avatar variant='rounded' sx={{ width: 44, height: 44, bgcolor: '#e8f5e9', color: '#2e7d32', borderRadius: 2 }}>
                                    <FontAwesomeIcon icon={faUserCheck} size="sm" />
                                </Avatar>
                                <Box flexGrow={1}>
                                    <Typography variant="caption" sx={{ color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        Asociados Activos
                                    </Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#222', my: 0.2 }}>
                                        {stats.active}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Card>
                    </Grid>

                    {/* KPI 3: Inactivos */}
                    <Grid item xs={12} sm={4}>
                        <Card {...cardStyle}>
                            <Stack direction="row" spacing={2} alignItems="center">
                                <Avatar variant='rounded' sx={{ width: 44, height: 44, bgcolor: '#fff3e0', color: '#ed6c02', borderRadius: 2 }}>
                                    <FontAwesomeIcon icon={faUserMinus} size="sm" />
                                </Avatar>
                                <Box flexGrow={1}>
                                    <Typography variant="caption" sx={{ color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        Asociados Inactivos
                                    </Typography>
                                    <Typography variant="h5" sx={{ fontWeight: 700, color: '#222', my: 0.2 }}>
                                        {stats.inactive}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Card>
                    </Grid>
                </Grid>
            )}

            {/* --- CONTENIDO PRINCIPAL (Tabla o Empty State) --- */}
            {employees.length !== 0 ? (
                // La tabla se renderiza sin Card, tal como solicitaste
                <EmployeesTable 
                    employees={employees} 
                    onEdit={handleOpen} 
                    onDelete={handleDelete}
                />
            ) : (
                !isLoading && (
                    /* EMPTY STATE (Estado vacío cuando no hay empleados) */
                    <Card sx={{ border: '1px solid #e0e0e0', borderRadius: 3, boxShadow: 'none', py: 10, display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
                        <Box sx={{ width: 80, height: 80, bgcolor: '#f8f9fa', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 3 }}>
                            <FontAwesomeIcon icon={faUserPlus} size="3x" color="#ccc" />
                        </Box>
                        <Typography variant="h6" fontWeight={700} sx={{ color: '#333' }} gutterBottom>
                            Tu equipo aún no tiene miembros
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#666', maxWidth: 450, mb: 4 }}>
                            Agrega a tus asociados de ventas, atención al cliente o profesionales para que puedan gestionar reservas, pedidos y horarios.
                        </Typography>
                        <Button
                            variant='outlined'
                            color="primary"
                            startIcon={<FontAwesomeIcon icon={faPlus} />}
                            onClick={() => handleOpen()}
                            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                        >
                            Vincular primer empleado
                        </Button>
                    </Card>
                )
            )}

            {/* --- MODAL DE CREACIÓN / EDICIÓN --- */}
            <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ borderBottom: '1px solid #eee', pb: 2 }}>
                    <Typography variant="h6" fontWeight="bold">
                        {form.id ? 'Gestionar Asociado' : 'Vincular Asociado'}
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <Tabs value={tabValue} onChange={(e,v)=>setTabValue(v)} sx={{ mb: 3, borderBottom: 1, borderColor: 'divider', minHeight: 40 }} indicatorColor="primary">
                        <Tab label="Perfil" sx={{ textTransform: 'none', fontWeight: 'bold' }} icon={<BadgeIcon/>} iconPosition="start"/>
                        <Tab label="Horario Laboral" disabled={!form.id} sx={{ textTransform: 'none', fontWeight: 'bold' }} icon={<AccessTimeIcon/>} iconPosition="start"/>
                    </Tabs>
                    
                    {tabValue === 0 && (
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField 
                                    fullWidth 
                                    label="Correo Electrónico" 
                                    name="email" 
                                    placeholder="ejemplo@correo.com" 
                                    value={form.email} 
                                    onChange={handleChange} 
                                    onBlur={handleEmailBlur} 
                                    disabled={!!form.id} 
                                    size="small"
                                    InputProps={{ 
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                {isSearching && <CircularProgress size={20} />}
                                                {emailStatus === 'found' && <CheckCircleIcon color="success" />}
                                                {emailStatus === 'not_found' && <Typography variant="caption" color="primary" fontWeight="bold">Nuevo</Typography>}
                                            </InputAdornment>
                                        )
                                    }}
                                />
                                {emailStatus === 'not_found' && !form.id && (
                                    <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                        El usuario no existe en QUD. Registraremos sus datos básicos.
                                    </Typography>
                                )}
                            </Grid>
                            
                            {(emailStatus === 'not_found' || form.id) && (
                                <>
                                    <Grid item xs={6}>
                                        <TextField fullWidth size="small" label="Nombre" name="first_name" value={form.first_name} onChange={handleChange} />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <TextField fullWidth size="small" label="Apellido" name="last_name" value={form.last_name} onChange={handleChange} />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField fullWidth size="small" label="Teléfono" name="phone" value={form.phone} onChange={handleChange} />
                                    </Grid>
                                </>
                            )}
                            
                            {emailStatus !== 'initial' && (
                                <>
                                    <Grid item xs={12}>
                                        <Divider sx={{ my: 1 }} />
                                    </Grid>
                                    <Grid item xs={8}>
                                        <TextField fullWidth size="small" label="Cargo / Rol (Ej. Barbero, Cajero)" name="position" value={form.position} onChange={handleChange} />
                                    </Grid>
                                    <Grid item xs={4}>
                                        <FormControl fullWidth size="small">
                                            <InputLabel>Estado</InputLabel>
                                            <Select name="status" value={form.status} label="Estado" onChange={handleChange}>
                                                <MenuItem value={1}>Activo</MenuItem>
                                                <MenuItem value={0}>Inactivo</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    )}

                    {tabValue === 1 && form.id && (
                        <Box sx={{ mt: 1 }}>
                            <WeeklyScheduleEditor resourceType="employee" resourceId={form.id} />
                        </Box>
                    )}
                </DialogContent>
                
                <DialogActions sx={{ p: 3, borderTop: '1px solid #eee' }}>
                    <Button onClick={handleClose} sx={{ color: 'text.secondary', textTransform: 'none', fontWeight: 'bold' }}>Cancelar</Button>
                    {tabValue === 0 && (
                        <LoadingButton 
                            variant="contained" 
                            onClick={handleSubmit} 
                            loading={isSaving} 
                            disabled={!form.id && emailStatus === 'initial'} 
                            disableElevation
                            sx={{ textTransform: 'none', fontWeight: 'bold', borderRadius: 2 }}
                        >
                            {form.id ? 'Guardar Cambios' : 'Vincular Asociado'}
                        </LoadingButton>
                    )}
                </DialogActions>
            </Dialog>

            <ResponseComponent
                show={response.show}
                success={response.success}
                message={response.message}
                onClick={() => setResponse({...response, show: false})}
            />
        </Container>
    );
}